import React, { useEffect, useState } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import { useAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';
import DeliveryManagement from './Delivery/DeliveryManagement';
import LogisticsManagementAtom, { fetchAliesData, fetchCustomFields, fetchDeliveriesAtom, fetchDeliveriesDownloadAtom, fetchLaydownData, fetchPartsList, fetchScopeLaydownWiseAtom, setCurrentLaydownAtom } from '../../atoms/logistics/LogisticsManagementAtom';
import dashboardAtoms, { fetchBlocks, fetchMapRender } from '../../atoms/Dashboard/DashboardAtom'
import InventoryComponent from './Inventory';
import Scope from './Scope';

export default function FullWidthTabs() {
  const navigate = useNavigate();
  const params = useParams();
  const { tab, id } = params; 

  const [value, setValue] = useState("deliveries");
  const [, fetchDeliveries] = useAtom(fetchDeliveriesAtom);
  const [, fetchDownloadDeliveries] = useAtom(fetchDeliveriesDownloadAtom)
  const [, setFields] = useAtom(fetchCustomFields);

  const [data] = useAtom(LogisticsManagementAtom);
  const [, setFetchAliesData] = useAtom(fetchAliesData);
  const { DeliveryData, laydownList, partList, inventoryData, laydownWiseScope, customFields, downloadData } = data;
  const [currentLaydown, setCurrentLaydown] = useState({});
  const [dashboardData] = useAtom(dashboardAtoms);
  const [, setBlocks] = useAtom(fetchBlocks);
  const [, setMap] = useAtom(fetchMapRender);
  const [, setScopeLaydownWise] = useAtom(fetchScopeLaydownWiseAtom)
  const [loader, setLoader] = useState(false);
  const { bom_laydown_data } = inventoryData;
  const [, setLaydown] = useAtom(fetchLaydownData);
  const [, setCurrentLaydowns] = useAtom(setCurrentLaydownAtom);
  const [, setParts] = useAtom(fetchPartsList);
  const [editScope, setEditScope] = useState('scope-list');


  useEffect(() => {
    if (tab && ["inventory", "deliveries", "scope"].includes(tab)) {
      setValue(tab);
    } else {
      navigate("/dashboard/logistics-management/deliveries", { replace: true });
      setValue("deliveries");
    }
  }, [tab, navigate]);


  useEffect(() => {
    if (!laydownList?.length) return;
  
    const parsedId = parseInt(id);
  
    const laydownFromUrl = laydownList.find(l => l.id === parsedId);
  
    if (id && laydownFromUrl.id) {
      setCurrentLaydown(laydownFromUrl);
      setCurrentLaydowns(laydownFromUrl);
    } else if (!id && value === "inventory" && !currentLaydown?.id) {
      setCurrentLaydown(laydownList[0]);
      setCurrentLaydowns(laydownList[0]);
      navigate(`/dashboard/logistics-management/${value}/${laydownList[0].id}`, { replace: true });
    } else if (!id && value === "scope" && !currentLaydown?.id) {
      setCurrentLaydown(laydownList[0]);
      setCurrentLaydowns(laydownList[0]);
      navigate(`/dashboard/logistics-management/${value}/${laydownList[0].id}/scope-list`, { replace: true });
    } else if ((value === "scope" || value === "deliveries") && !currentLaydown?.id) {
      setCurrentLaydown({});
      setCurrentLaydowns({});
    }
    // eslint-disable-next-line
  }, [laydownList]);
  

  const handleChange = (event, newValue) => {
    event.preventDefault();
    setValue(newValue);
    
    if (newValue === "deliveries") {
      navigate(`/dashboard/logistics-management/${newValue}`);
      setCurrentLaydown({}); 
    } else if (newValue === "inventory") {
      if (currentLaydown?.id) {
        navigate(`/dashboard/logistics-management/${newValue}/${currentLaydown.id}`);
      } else if (laydownList?.length) {
        const defaultLaydown = laydownList[0];
        setCurrentLaydown(defaultLaydown);
        navigate(`/dashboard/logistics-management/${newValue}/${defaultLaydown.id}`);
      } else {
        navigate(`/dashboard/logistics-management/${newValue}`);
      }
    } else if (newValue === "scope") {
      if (currentLaydown?.id) {
        navigate(`/dashboard/logistics-management/${newValue}/${currentLaydown.id}`);
      } else {
        navigate(`/dashboard/logistics-management/${newValue}`);
        setCurrentLaydown({}); 
      }
    } else {
      navigate(`/dashboard/logistics-management/${newValue}`);
    }
  };

  const handleLaydownChange = (newLaydown) => {
    setCurrentLaydown(newLaydown);
    if ((value === "inventory" || value === "scope") && newLaydown?.id) {
      navigate(`/dashboard/logistics-management/${value}/${newLaydown.id}/scope-list`, { replace: true });
    }
  };

  const fetchScopeData = async () => {
    if (!currentLaydown?.id) return;
    try {
      await setScopeLaydownWise()
    } catch (error) {
      console.error("Something went wrong with scope")
    }
  }

  useEffect(() => {
    if (value === "scope" && currentLaydown?.id && laydownWiseScope.length === 0) {
      fetchScopeData()
    }
    setParts(currentLaydown?.id)
    // eslint-disable-next-line
  }, [value, currentLaydown?.id])

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoader(true);
      try {
        await Promise.all([
          fetchDeliveries(),
          fetchDownloadDeliveries(),
          setFields(),
          setFetchAliesData("delivery", "delivery_reference")
        ]);
      } catch (error) {
        console.error("Error fetching logistics data:", error);
      } finally {
        setLoader(false);
      }
    };

    fetchInitialData();
    // eslint-disable-next-line
  }, [fetchDeliveries]);

  useEffect(() => {
    if (value === "scope") {
      const fetchScopeData = async () => {
        setLoader(true)
        try {
          await Promise.all([
            setBlocks(''),
            setMap(''),
            setLaydown()
          ])
        } catch (err) {
          console.error(err)
        } finally {
          setLoader(false)
        }
      }
      fetchScopeData()
    }
    setLaydown()
  }, [value, setBlocks, setMap, setLaydown])

  

  return (
    <Box px={4} py={1}>
      <Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label="Inventory" value="inventory" />
            <Tab label="Deliveries" value="deliveries" />
            <Tab label="Scope" value="scope" />
          </Tabs>
        </Box>
        <Box sx={{ py: 1 }}>
          {value === "inventory" && (
            <InventoryComponent
              title={value}
              laydownList={laydownList}
              current={currentLaydown}
              setCurrent={handleLaydownChange} 
              loader={loader}
              data={bom_laydown_data}
              setLaydown={setLaydown}
              setCurrentLaydown={setCurrentLaydown}
              aliasName={customFields?.field_alias?.field_alias_name}
            />
          )}
          {value === "deliveries" && (
            <DeliveryManagement
              loader={loader}
              data={DeliveryData}
              title={value}
              laydownList={laydownList}
              partList={partList}
              aliasName={customFields?.field_alias?.field_alias_name}
              customFields={customFields}
              setFields={setFields}
              downLoadData={downloadData}
              current={currentLaydown}
            />
          )}
          {value === "scope" && (
            <Scope
              data={dashboardData}
              value={data}
              loader={loader}
              scopeLaydownProgress={laydownWiseScope}
              currentLaydown={currentLaydown}
              setCurrentLaydown={handleLaydownChange} 
              laydownList={laydownList}
              setLoader={setLoader}
              editScope={editScope} 
              setEditScope={setEditScope}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}